import { Link } from "react-router-dom";
import { useEffect, useState, useRef } from "react";
import SEO from "@/components/SEO";
import { ArrowLeft, Star, CheckCircle, Hammer, Columns3, Shield, Clock, Award, ChevronLeft, ChevronRight, Play, FileDown, Users, Sparkles, BadgeCheck, Phone } from "lucide-react";
import { Button } from "@/components/ui/button";
import Layout from "@/components/Layout";
import SectionTitle from "@/components/SectionTitle";
import CustomerReviews from "@/components/CustomerReviews";
import CostEstimator from "@/components/CostEstimator";
import { supabase } from "@/integrations/supabase/client";
import heroImg from "@/assets/hero-workshop.webp";
import carpentryImg from "@/assets/service-carpentry.webp";
import aluminumImg from "@/assets/service-aluminum.webp";
import ironImg from "@/assets/service-iron.webp";

const stats = [
  { value: "500+", label: "مشروع منجز", icon: CheckCircle },
  { value: "15+", label: "سنة خبرة", icon: Award },
  { value: "300+", label: "عميل سعيد", icon: Users },
  { value: "100%", label: "ضمان الجودة", icon: Shield },
];

const services = [
  { title: "أعمال النجارة", desc: "أبواب، مطابخ، غرف نوم، وجميع الأعمال الخشبية بأجود الخامات", img: carpentryImg, icon: Hammer },
  { title: "أعمال الألمنيوم", desc: "نوافذ وأبواب ألمنيوم، واجهات محلات بتصاميم عصرية ومقاطع متنوعة", img: aluminumImg, icon: Columns3 },
  { title: "أعمال الحديد", desc: "بوابات حديد، درابزين، سلالم، هياكل معدنية مشغولة بدقة عالية", img: ironImg, icon: Shield },
];

type GalleryItem = {
  id: string;
  title: string;
  category: string;
  image_url: string;
};

export default function Index() {
  const [galleryItems, setGalleryItems] = useState<GalleryItem[]>([]);
  const [currentSlide, setCurrentSlide] = useState(0);

  useEffect(() => {
    supabase
      .from("gallery_items")
      .select("id, title, category, image_url")
      .eq("is_active", true)
      .order("sort_order", { ascending: true })
      .limit(6)
      .then(({ data }) => setGalleryItems((data || []) as GalleryItem[]));
  }, []);

  useEffect(() => {
    if (galleryItems.length === 0) return;
    const interval = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % galleryItems.length);
    }, 4000);
    return () => clearInterval(interval);
  }, [galleryItems.length]);

  return (
    <Layout>
      <SEO
        title="مجمع الشويع الصناعي | نجارة وألمنيوم وحديد - صعدة"
        description="مجمع الشويع الصناعي في صعدة، اليمن - نجارة وألمنيوم وحديد بأعلى جودة وأفضل الأسعار. أبواب، نوافذ، مطابخ، غرف نوم، واجهات، تفصيل وصيانة."
        path="/"
      />
      {/* Hero Section */}
      <section className="relative min-h-[88vh] flex items-center overflow-hidden">
        <div className="absolute inset-0">
          <img src={heroImg} alt="مجمع الشويع الصناعي" className="w-full h-full object-cover" width={1920} height={1080} fetchPriority="high" />
          <div className="absolute inset-0 bg-gradient-to-l from-background/95 via-background/75 to-background/30" />
        </div>
        <div className="container relative z-10">
          <div className="max-w-2xl">
            <div className="inline-flex items-center gap-2 bg-primary/10 border border-primary/20 rounded-full px-4 py-2 mb-5 animate-fade-in">
              <BadgeCheck className="h-4 w-4 text-primary" />
              <span className="text-sm text-primary font-semibold">الموقع الرسمي والوحيد</span>
            </div>
            <h1 className="text-4xl md:text-6xl font-black text-foreground leading-tight mb-5 animate-fade-in-up">
              مجمع <span className="text-gradient-woody">الشويع</span> الصناعي — نجارة وألمنيوم وحديد
            </h1>

            {/* Hero prominent text */}
            <div className="bg-gradient-woody rounded-xl p-5 md:p-6 mb-7 shadow-woody animate-fade-in-up" style={{ animationDelay: "0.15s" }}>
              <p className="text-base md:text-lg text-primary-foreground leading-relaxed font-semibold">
                متخصصون في النجارة والألمنيوم والحديد. نصنع لك بيتك بأعلى جودة وأفضل الأسعار في مديرية حيدان، صعدة.
              </p>
            </div>

            <div className="flex flex-wrap gap-3 animate-fade-in-up" style={{ animationDelay: "0.3s" }}>
              <Link to="/طلب-تصميم">
                <Button size="lg" className="bg-gradient-woody text-primary-foreground hover:opacity-90 text-base px-8 shadow-woody">
                  اطلب تصميمك الآن
                  <ArrowLeft className="h-5 w-5 mr-2" />
                </Button>
              </Link>
              <Link to="/معرض-الأعمال">
                <Button size="lg" variant="outline" className="text-base px-8 border-primary/30 hover:bg-primary/5">
                  <Play className="h-4 w-4 ml-2" />
                  شاهد أعمالنا
                </Button>
              </Link>
            </div>

            {/* Trust badges */}
            <div className="flex items-center gap-4 mt-6 animate-fade-in-up" style={{ animationDelay: "0.5s" }}>
              <div className="flex items-center gap-1.5 text-sm text-foreground/70">
                <Shield className="h-4 w-4 text-primary" />
                <span>ضمان الجودة</span>
              </div>
              <div className="w-px h-4 bg-border" />
              <div className="flex items-center gap-1.5 text-sm text-foreground/70">
                <Clock className="h-4 w-4 text-primary" />
                <span>سرعة التنفيذ</span>
              </div>
              <div className="w-px h-4 bg-border" />
              <div className="flex items-center gap-1.5 text-sm text-foreground/70">
                <Star className="h-4 w-4 text-primary" />
                <span>15+ سنة خبرة</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Stats */}
      <section className="py-6 -mt-16 relative z-20">
        <div className="container">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-4">
            {stats.map((stat, i) => (
              <div key={i} className="bg-card border border-border rounded-xl p-4 md:p-5 text-center shadow-lg hover:shadow-card-hover transition-all hover:-translate-y-1 duration-300">
                <stat.icon className="h-6 w-6 text-primary mx-auto mb-2" />
                <p className="text-2xl md:text-3xl font-black text-foreground">{stat.value}</p>
                <p className="text-sm text-muted-foreground">{stat.label}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Services */}
      <section className="py-16 md:py-20">
        <div className="container">
          <SectionTitle title="خدماتنا المتميزة" subtitle="نقدم خدمات شاملة ومتكاملة بأيدي خبراء متخصصين" />
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-8">
            {services.map((s, i) => (
              <div key={i} className="group rounded-2xl overflow-hidden border border-border bg-card hover:shadow-card-hover transition-all duration-300 hover:-translate-y-1">
                <div className="aspect-[4/3] overflow-hidden relative">
                  <img src={s.img} alt={s.title} loading="lazy" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
                  <div className="absolute inset-0 bg-gradient-to-t from-background/80 to-transparent" />
                  <div className="absolute bottom-4 right-4 w-12 h-12 rounded-full bg-primary/90 flex items-center justify-center shadow-lg">
                    <s.icon className="h-6 w-6 text-primary-foreground" />
                  </div>
                </div>
                <div className="p-5 md:p-6">
                  <h3 className="text-xl font-bold text-foreground mb-2">{s.title}</h3>
                  <p className="text-muted-foreground text-sm leading-relaxed mb-4">{s.desc}</p>
                  <Link to="/خدماتنا">
                    <Button variant="ghost" className="text-primary hover:text-primary px-0 font-semibold">
                      المزيد <ArrowLeft className="h-4 w-4 mr-1" />
                    </Button>
                  </Link>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Gallery Preview */}
      {galleryItems.length > 0 && (
        <section className="py-16 md:py-20 bg-muted/30">
          <div className="container">
            <SectionTitle title="من أعمالنا" subtitle="نماذج مختارة من مشاريعنا المنجزة" />
            <div className="relative max-w-4xl mx-auto">
              <div className="aspect-video rounded-2xl overflow-hidden border border-border shadow-lg relative">
                {galleryItems.map((item, i) => {
                  // Use Supabase image transformation to deliver appropriately-sized WebP
                  const optimized = item.image_url.includes("/storage/v1/object/public/")
                    ? item.image_url.replace("/storage/v1/object/public/", "/storage/v1/render/image/public/") + "?width=700&quality=65&format=webp"
                    : item.image_url;
                  return (
                    <img
                      key={item.id}
                      src={optimized}
                      alt={item.title}
                      loading={i === 0 ? "eager" : "lazy"}
                      width={900}
                      height={506}
                      className={`absolute inset-0 w-full h-full object-cover transition-opacity duration-700 ${i === currentSlide ? "opacity-100" : "opacity-0"}`}
                    />
                  );
                })}
                <div className="absolute inset-0 bg-gradient-to-t from-background/60 to-transparent" />
                <div className="absolute bottom-4 md:bottom-6 right-4 md:right-6 left-4 md:left-6 flex items-end justify-between">
                  <div>
                    <h3 className="text-lg md:text-xl font-bold text-foreground">{galleryItems[currentSlide]?.title}</h3>
                    <span className="text-sm text-primary font-medium">{galleryItems[currentSlide]?.category}</span>
                  </div>
                  <div className="flex gap-2">
                    <button onClick={() => setCurrentSlide((prev) => (prev - 1 + galleryItems.length) % galleryItems.length)} className="w-9 h-9 md:w-10 md:h-10 rounded-full bg-background/80 flex items-center justify-center hover:bg-background transition-colors">
                      <ChevronRight className="h-5 w-5 text-foreground" />
                    </button>
                    <button onClick={() => setCurrentSlide((prev) => (prev + 1) % galleryItems.length)} className="w-9 h-9 md:w-10 md:h-10 rounded-full bg-background/80 flex items-center justify-center hover:bg-background transition-colors">
                      <ChevronLeft className="h-5 w-5 text-foreground" />
                    </button>
                  </div>
                </div>
              </div>
              <div className="flex justify-center gap-2 mt-4">
                {galleryItems.map((_, i) => (
                  <button key={i} onClick={() => setCurrentSlide(i)} className={`w-2.5 h-2.5 rounded-full transition-all ${i === currentSlide ? "bg-primary w-8" : "bg-border"}`} />
                ))}
              </div>
            </div>
            <div className="text-center mt-8">
              <Link to="/معرض-الأعمال">
                <Button variant="outline" size="lg" className="border-primary/30 hover:bg-primary/5 font-semibold">
                  شاهد جميع الأعمال <ArrowLeft className="h-4 w-4 mr-2" />
                </Button>
              </Link>
            </div>
          </div>
        </section>
      )}

      {/* Cost Estimator */}
      <section className="py-16 md:py-20">
        <div className="container max-w-lg">
          <SectionTitle title="احسب تكلفة مشروعك" subtitle="أداة تقدير فورية للتكلفة التقريبية" />
          <CostEstimator />
        </div>
      </section>

      {/* Reviews */}
      <CustomerReviews />

      {/* CTA */}
      <section className="py-16 md:py-20">
        <div className="container">
          <div className="bg-gradient-woody rounded-2xl p-8 md:p-14 text-center relative overflow-hidden">
            <div className="absolute inset-0 opacity-10">
              <div className="absolute top-10 left-10 w-40 h-40 border border-primary-foreground rounded-full" />
              <div className="absolute bottom-10 right-10 w-60 h-60 border border-primary-foreground rounded-full" />
            </div>
            <div className="relative z-10">
              <h2 className="text-2xl md:text-4xl font-black text-primary-foreground mb-4">جاهز لبدء مشروعك؟</h2>
              <p className="text-primary-foreground/80 text-base md:text-lg mb-8 max-w-xl mx-auto">
                تواصل معنا الآن واحصل على استشارة مجانية وعرض سعر مخصص لمشروعك
              </p>
              <div className="flex flex-wrap justify-center gap-3">
                <Link to="/طلب-تصميم">
                  <Button size="lg" className="bg-background text-foreground hover:bg-background/90 text-base px-8 shadow-md">
                    اطلب تصميمك الآن
                  </Button>
                </Link>
                <Link to="/حجز-موعد">
                  <Button size="lg" variant="outline" className="border-primary-foreground/30 text-primary-foreground hover:bg-primary-foreground/10 text-base px-8">
                    <Clock className="h-4 w-4 ml-2" />
                    احجز موعد
                  </Button>
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Quick Links */}
      <section className="py-10 md:py-12 bg-muted/30">
        <div className="container">
          <h3 className="text-lg font-bold text-foreground text-center mb-6">استكشف المزيد</h3>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-4">
            {[
              { label: "تتبع طلبك", path: "/تتبع-الطلب", icon: "📦" },
              { label: "برنامج الولاء", path: "/برنامج-الولاء", icon: "🎁" },
              { label: "العروض", path: "/العروض", icon: "🏷️" },
              { label: "تحميل الكتالوج", path: "/معرض-الأعمال", icon: "📥" },
              { label: "المدونة", path: "/المدونة", icon: "📝" },
              { label: "مقارنة المنتجات", path: "/مقارنة", icon: "⚖️" },
              { label: "تصميم ذكي AI", path: "/تصميم-ذكي", icon: "✨" },
              { label: "برنامج الإحالة", path: "/الإحالة", icon: "🤝" },
            ].map((item) => (
              <Link key={item.path} to={item.path} className="bg-card border border-border rounded-xl p-4 text-center hover:shadow-card-hover hover:-translate-y-1 transition-all duration-300 group">
                <span className="text-2xl block mb-1">{item.icon}</span>
                <p className="text-sm font-semibold text-foreground group-hover:text-primary transition-colors">{item.label}</p>
              </Link>
            ))}
          </div>
        </div>
      </section>
    </Layout>
  );
}
